import { useState, useMemo, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { UserFormModel } from '../UserFormModel';
import { UserType, UserLocationDTO } from '../../../../api/users/users.contracts';
import { UserModel } from '../../../../types/UserModel';
import { LocationModel } from '@/modules/locations/types/LocationModel';
import { useRoles } from '@/modules/users/submodules/roles/hooks/useRoles';
import { APPLICATION_ADMIN_ROLE_NAME } from '@/modules/users/submodules/roles/api/roles.contracts';

interface UseLocationManagementProps {
  form: UseFormReturn<UserFormModel>;
  userId?: number;
  userData?: UserModel;
}

export const useLocationManagement = ({ form, userId, userData }: UseLocationManagementProps) => {
  const { watch, setValue, getValues } = form;
  const { roles } = useRoles();

  // Store added locations with their full data (name, code)
  const [addedLocations, setAddedLocations] = useState<Map<number, { name: string; code: string }>>(new Map());

  // Watch form values
  const userType = watch('type');
  const formLocationIds = watch('locationIds') || [];
  const formRoleIds = watch('roleIds') || [];
  const isGuestUser = userType === UserType.GUEST;

  // Derived from the watched roleIds rather than userData so the locations card appears and
  // disappears as soon as the role is changed in the form, before anything is saved.
  const isAdminUser = useMemo(
    () => roles.some((role) => formRoleIds.includes(role.dto.id) && role.dto.name === APPLICATION_ADMIN_ROLE_NAME),
    [roles, formRoleIds],
  );

  // Create combined userLocations from original data + any new ones added via form
  const userLocations = useMemo((): UserLocationDTO[] => {
    const originalUserLocations = userData?.userLocations || [];
    const existingLocationIds = originalUserLocations.map((ul) => ul.locationId);

    // Add any new locations that are in formLocationIds but not in original data
    const newLocationEntries: UserLocationDTO[] = formLocationIds
      .filter((id: number) => !existingLocationIds.includes(id))
      .map((id: number) => {
        const addedLocationData = addedLocations.get(id);
        return {
          id: Date.now() + id, // Temporary ID
          userId: userId || 0,
          locationId: id,
          locationName: addedLocationData?.name || `Location ${id}`,
          locationCode: addedLocationData?.code || `LOC-${id}`,
        };
      });

    // Filter original locations that are still in formLocationIds
    const remainingOriginalLocations = originalUserLocations.filter((ul) => formLocationIds.includes(ul.locationId));

    return [...remainingOriginalLocations, ...newLocationEntries];
  }, [userData?.userLocations, formLocationIds, userId, addedLocations]);

  // Handle removing a location
  const handleRemoveLocation = (locationIdToRemove: number) => {
    const currentLocationIds = getValues('locationIds') || [];
    const updatedLocationIds = currentLocationIds.filter((id: number) => Number(id) !== Number(locationIdToRemove));

    // Clean up stored location data
    setAddedLocations((prev) => {
      const newMap = new Map(prev);
      newMap.delete(locationIdToRemove);
      return newMap;
    });

    setValue('locationIds', updatedLocationIds);
  };

  // Handle adding a location
  const handleAddLocation = (location: LocationModel) => {
    const currentLocationIds = getValues('locationIds') || [];
    const newLocationIds = [...currentLocationIds, location.dto.id];

    // Store the location data for display purposes
    setAddedLocations(
      (prev) =>
        new Map(
          prev.set(location.dto.id, {
            name: location.dto.name,
            code: location.dto.code,
          }),
        ),
    );

    setValue('locationIds', newLocationIds);
  };

  // Clear roleIds when switching to guest user type
  useEffect(() => {
    if (isGuestUser) {
      setValue('roleIds', []);
    }
  }, [isGuestUser, setValue]);

  // Clear locationIds when the user becomes an administrator. Administrators are never
  // location-scoped, so the card is hidden for them - without this the ids would linger in a
  // field nobody can see or edit. Note this deliberately does NOT fire on a plain
  // GUEST -> OPERATOR switch: operators can be location-scoped too, so their assignments
  // must survive a type change.
  useEffect(() => {
    if (isAdminUser) {
      setValue('locationIds', []);
      setAddedLocations(new Map());
    }
  }, [isAdminUser, setValue]);

  return {
    userLocations,
    handleRemoveLocation,
    handleAddLocation,
    isGuestUser,
    isAdminUser,
  };
};
